import { StripeApi } from "@/api/stripe.api";
import { clearCheckoutData } from "@/stores/checkout";
import { CartService } from "@/stores/cart";
import { clearSharedCartSession } from "@/stores/sharedCart";
import { pushPurchase, purchaseAlreadySent } from "@/utils/analytics";
import { StytchClient } from "@/stores/stytch";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { Divider } from "@mantine/core";
import { CheckCircle } from "@phosphor-icons/react";
import Cookies from "js-cookie";
import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

const PaymentStatus = () => {
  const location = useLocation();

  const isSuccess = location.pathname === "/payment/success";

  useEffect(() => {
    if (!isSuccess) return;

    // GA4 e-commerce purchase event (WebFX, 2026-08-31). The Stripe redirect
    // carries ?session_id={CHECKOUT_SESSION_ID}; fetch the session for the
    // authoritative total/currency and the server-built GA4 items, push the
    // purchase event, THEN clear the cart. Order matters. Guarded against
    // refresh/back-button double fires (localStorage + GA4 transaction_id
    // dedupe). Analytics is best-effort: the cart is cleared no matter what.
    const sessionId = new URLSearchParams(location.search).get("session_id");
    const clearAll = () => {
      clearCheckoutData();
      CartService.clearAdminWorkspace();
      clearSharedCartSession();
    };

    if (!sessionId || purchaseAlreadySent(sessionId)) {
      clearAll();
      return;
    }

    let cancelled = false;
    StripeApi.getCheckout(sessionId)
      .then(({ data }) => {
        if (cancelled) return;
        const amountTotal = data.session?.amount_total;
        if (typeof amountTotal === "number") {
          pushPurchase({
            sessionId,
            value: amountTotal / 100,
            currency: (data.session.currency || "usd").toUpperCase(),
            items: data.ga4Items || [],
          });
        }
      })
      .catch(error => {
        console.warn("GA4 purchase tracking failed:", error);
      })
      .finally(() => {
        if (!cancelled) clearAll();
      });
    return () => {
      cancelled = true;
    };
  }, [isSuccess, location.search]);

  // Get user's email from various sources
  const userEmail = useMemo(() => {
    // Try to get email from URL parameters first (in case passed from Stripe)
    const urlParams = new URLSearchParams(location.search);
    const emailFromUrl = urlParams.get("email");
    if (emailFromUrl) return emailFromUrl;

    // Try to get from cookies (set during authentication)
    const emailFromCookies = Cookies.get("stytch_user_email");
    if (emailFromCookies) return emailFromCookies;

    // Try to get from Stytch client directly
    try {
      const user = StytchClient.user.getSync();
      if (user?.emails?.[0]?.email) return user.emails[0].email;
    } catch (error) {
      console.log("Could not get user email from Stytch:", error);
    }

    // Fallback to generic message
    return null;
  }, [location.search]);

  const data = {
    Icon: isSuccess ? CheckCircle : XCircleIcon,
    title: isSuccess ? "Congratulations!" : "Payment Canceled",
    message: isSuccess
      ? userEmail
        ? `Congratulations! Thank you for your order. We will keep you updated on the order status on your email ${userEmail}`
        : "Congratulations! Thank you for your order. We will keep you updated on the order status via email."
      : "Thank you for your attempt!",
    link: isSuccess ? "/upload" : `/upload${location.search}`,
  };

  return (
    <div
      className="m-auto flex h-screen w-[418px] flex-col items-center justify-center px-4 text-center"
      data-clarity-mask="true"
    >
      <data.Icon
        className={`mb-5 size-12 ${isSuccess ? "text-[#4AA0A0]" : "text-error"}`}
      />
      <h2 className="mb-4 text-[22px] font-bold leading-6">{data.title}</h2>
      <p className="mb-12 leading-[18px] text-[#636363]">{data.message}</p>
      <Divider color="#00000014" className="w-full" />
      <Link
        to={data.link}
        className="text-md mt-5 flex h-[50px] w-[calc(100%-2.5rem)] items-center justify-center rounded-full bg-[#4AA0A0] font-bold text-white hover:bg-[#4AA0A0]"
      >
        GO BACK TO HOMEPAGE
      </Link>
    </div>
  );
};

export default PaymentStatus;
